import { useDebounce } from '@repo/toolkit/use-debounce'
import DraggableList from '@repo/ui/draggable-list'
import { useEffect, useRef, useState } from 'react'

import DraggableListCard from './draggable-list-card'

interface ManagedDraggableListProps<T extends { id: number | string; position?: number }> {
  items: T[]
  onUpdatePositions: (items: Array<{ id: T['id']; position: number }>) => void
  renderContent: (item: T) => React.ReactNode
  onEdit?: (item: T) => void
  onDelete?: (item: T) => void
  isUpdating?: boolean
  disabled?: boolean
}

const ManagedDraggableList = <T extends { id: number | string; position?: number }>({
  items,
  onUpdatePositions,
  renderContent,
  onEdit,
  onDelete,
  isUpdating,
  disabled
}: ManagedDraggableListProps<T>) => {
  const [isDragging, setIsDragging] = useState(false)
  const pendingItemsRef = useRef<Array<{ id: T['id']; position: number }> | null>(null)
  const previousItemsRef = useRef<T[]>(items)

  const { debouncedCallback: debouncedUpdatePositions, cancel: cancelUpdatePositions } =
    useDebounce(onUpdatePositions, 1000)

  useEffect(() => {
    previousItemsRef.current = items
  }, [items])

  useEffect(() => {
    if (!isDragging && pendingItemsRef.current) {
      debouncedUpdatePositions(pendingItemsRef.current)
      pendingItemsRef.current = null
    }
  }, [isDragging, debouncedUpdatePositions])

  const handleReorder = (reorderedItems: T[]) => {
    const previousPositionMap = new Map(
      previousItemsRef.current.map((item) => [item.id, item.position])
    )

    const changedItems = reorderedItems
      .map((item, index) => ({
        id: item.id,
        position: index + 1,
        previousPosition: previousPositionMap.get(item.id)
      }))
      .filter((item) => item.position !== item.previousPosition)

    pendingItemsRef.current = changedItems
  }

  const handleDragStart = () => {
    setIsDragging(true)
    cancelUpdatePositions()
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }

  return (
    <DraggableList
      items={items}
      onReorder={handleReorder}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      disabled={disabled}
      renderItem={(item, isDragging, dragHandleProps) => (
        <DraggableListCard
          content={renderContent(item)}
          isDragging={isDragging}
          dragHandleProps={dragHandleProps}
          onEdit={onEdit ? () => onEdit(item) : undefined}
          onDelete={onDelete ? () => onDelete(item) : undefined}
          isDeleting={isUpdating} // Assuming isUpdating might affect delete state or we can add specific isDeleting prop
        />
      )}
      className="gap-3"
    />
  )
}

export default ManagedDraggableList
